import { Box } from '@mui/material';
import RoadmapMini1 from '../../assets/images/RoadmapMini1.png';
import RoadmapMini2 from '../../assets/images/RoadmapMini2.png';
import RoadmapMini3 from '../../assets/images/RoadmapMini3.png';
import '../../index.css';
import { useRef, useEffect, useMemo } from 'react';
import { GREEN_TEXT_ON_DARK } from 'Constants/AccessibleColors';

type MinimapPropTypes = {
  unitPercentComplete: number;
  scrollAreaSize: number; // should be 0..1
  scrollPosFromRoadmap: number; // should be 0..1
  setScrollPosFromRoadmap: React.Dispatch<React.SetStateAction<number>>;
  areaToPreventScroll: string;
  setAreaToPreventScroll: React.Dispatch<React.SetStateAction<string>>;
  // How far along the map the minimap box has been dragged: 0 = start, 1 = end.
  setPathScrollPos: React.Dispatch<React.SetStateAction<number>>;
};

const clamp01 = (v: number) => Math.max(0, Math.min(1, Number.isFinite(v) ? v : 0));

export const Minimap = (props: MinimapPropTypes) => {
  const {
    scrollPosFromRoadmap,
    scrollAreaSize,
    unitPercentComplete,
    areaToPreventScroll,
    setAreaToPreventScroll,
    setPathScrollPos,
  } = props;

  const scrollBox = useRef<HTMLDivElement>(null);

  // constants
  const containerWidth = 212;

  // guard scrollAreaSize
  const movingBoxWidth = useMemo(() => {
    const s = clamp01(scrollAreaSize);
    return s * containerWidth;
  }, [scrollAreaSize]);

  // minimap -> path: the box's place as a fraction of the minimap's travel, so
  // the box reaches the minimap's far end exactly when the map reaches its end,
  // whatever the window size. scrollLeft 0 is the box at the far right.
  useEffect(() => {
    const el = scrollBox.current;
    if (!el) return;

    const handleScroll = (e: Event) => {
      const target = e.target as HTMLDivElement;
      const maxScrollLeft = target.scrollWidth - target.clientWidth;
      if (maxScrollLeft <= 0) return;
      setPathScrollPos(clamp01(1 - target.scrollLeft / maxScrollLeft));
    };

    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => el.removeEventListener('scroll', handleScroll);
  }, [setPathScrollPos]);

  // roadmap -> minimap sync (THIS is what makes the slider move)
  useEffect(() => {
    const el = scrollBox.current;
    if (!el) return;

    const pct = clamp01(scrollPosFromRoadmap); // critical for Capacitor sanity
    const maxScrollLeft = Math.max(0, el.scrollWidth - el.clientWidth);

    // Your mapping: pct=0 => far right, pct=1 => far left (kept same behavior)
    el.scrollLeft = (1 - pct) * maxScrollLeft;
  }, [scrollPosFromRoadmap, movingBoxWidth]);

  // Pointer-drag to scroll (works on touch)
  const dragging = useRef(false);
  const lastX = useRef(0);

  const onPointerDown = (e: React.PointerEvent) => {
    if (areaToPreventScroll !== 'path') setAreaToPreventScroll('path');
    dragging.current = true;
    lastX.current = e.clientX;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragging.current || !scrollBox.current) return;
    const dx = lastX.current - e.clientX;
    scrollBox.current.scrollLeft += dx;
    lastX.current = e.clientX;
  };

  const endDrag = (e: React.PointerEvent) => {
    dragging.current = false;
    try {
      (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    } catch {
      // intentionally empty
    }
  };

  return (
    <Box
      className="minimap-box"
      sx={{
        width: `${containerWidth}px`,
        height: '70px',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 10,
      }}
    >
      <Box sx={{ position: 'relative', zIndex: 10, marginTop: '10px', height: '50px' }}>
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: 'black',
            zIndex: 0,
          }}
        />

        <Box
          ref={scrollBox}
          className="chatbox-scroll-container"
          sx={{
            width: '100%',
            height: '50px',
            background: 'transparent',
            overflowX: 'scroll',
            overflowY: 'hidden',
            position: 'relative',
            zIndex: 2,
          }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
        >
          <Box sx={{ width: `${424 - movingBoxWidth}px` }}>
            <Box
              sx={{
                marginLeft: `${212 - movingBoxWidth}px`,
                width: `${movingBoxWidth}px`,
                height: '50px',
                boxSizing: 'border-box',
                border: '3px solid #469F6A',
                borderRadius: '2px',
              }}
            />
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            margin: '5px',
            position: 'absolute',
            left: 0,
            top: 0,
            zIndex: 1,
            pointerEvents: 'none',
          }}
        >
          <Box sx={{ position: 'relative' }}>
            <img style={{ width: '100%' }} src={RoadmapMini1} />
          </Box>
          <Box sx={{ position: 'relative' }} />
          <Box sx={{ position: 'relative' }}>
            <img style={{ width: '100%' }} src={RoadmapMini2} />
          </Box>
          <Box sx={{ position: 'relative' }}>
            <img style={{ width: '100%' }} src={RoadmapMini3} />
          </Box>
        </Box>
      </Box>

      <Box
        className="minimap-progress-text"
        sx={{
          marginTop: '10px',
          fontWeight: 700,
          fontSize: '16px',
          letterSpacing: '0.01em',
          color: GREEN_TEXT_ON_DARK,
        }}
      >
        Unit {Math.round(100 * unitPercentComplete)}% Complete
      </Box>
    </Box>
  );
};
